import { SxProps } from '@mui/material';
import {
    GridActionsCellItemProps,
    GridAggregationFunction,
    GridAggregationModel,
    GridApiPremium,
    GridCallbackDetails,
    GridCellParams,
    GridColDef,
    GridColumnVisibilityModel,
    GridCsvExportOptions,
    GridDensity,
    GridFeatureMode,
    GridFilterModel,
    GridGroupingColDefOverride,
    GridListViewColDef,
    GridPaginationModel,
    GridPrintExportOptions,
    GridRenderCellParams,
    GridRowClassNameParams,
    GridRowEditStopParams,
    GridRowGroupingModel,
    GridRowId,
    GridRowModel,
    GridRowOrderChangeParams,
    GridRowParams,
    GridRowSelectionModel,
    GridSlotsComponent,
    GridSortModel,
    GridValidRowModel,
    MuiEvent
} from '@mui/x-data-grid-premium';
import type { ReactElement, RefObject, SyntheticEvent } from 'react';
import type { EntityName, FeatureName, SecurityAction } from '../../auth/Security';
import type { ROW_FOCUS_OUT_EDIT_ACTIONS } from '../../constants/dataGridConstants';
import { ColumnVisibilityPreset } from '../ColumnVisibiltyUtills';
import type { CreateDefaultsFieldMetadata, FacetForCreateDefaults } from '../createDefaultsFromViewContext';
import type { GridSettingsConfig } from './gridSettings';

export type RowFocusOutEditAction = (typeof ROW_FOCUS_OUT_EDIT_ACTIONS)[keyof typeof ROW_FOCUS_OUT_EDIT_ACTIONS];

export type DataGridCustomRowAction = {
    securityAction: SecurityAction;
    featureName?: FeatureName;
    labelToken: string;
    icon: ReactElement;
    isVisible?: (row: GridRenderCellParams['row']) => boolean;
    disabled?: boolean;
    onClick: (row: GridRenderCellParams['row']) => void;
    hideTooltip?: boolean;
};

export type UseDataGridActionsParams = {
    params: GridRenderCellParams;
    feature: string;
    /**
     * Optional only to support callers that still rely on the legacy permission fallback.
     * Make required once those callers forward it and the missing-entity fallback is removed.
     */
    entityName?: EntityName;
    onActivateOrInactivateHandle:
        | ((row: GridRenderCellParams['row'], canInactivate: boolean, canActivate: boolean) => void)
        | undefined;
    rowDetails: {
        activeRow: GridRowModel | undefined;
        isRowValid: ((id: string) => boolean) | undefined;
    };
    editHandlers: {
        onEdit: ((id: GridRenderCellParams['id'], row: GridRenderCellParams['row']) => void) | undefined;
        enableInlineEditing: boolean;
        handleEditClick?: (id: GridRenderCellParams['id']) => void;
        handleEditCommit?: (id: GridRenderCellParams['id']) => void;
        handleEditCancel?: (id: GridRenderCellParams['id']) => void;
    };
    hideEditActionWhenInlineEditing?: boolean;
    onShare: ((row: GridRenderCellParams['row']) => void) | undefined;
    deleteHandlers: {
        enableInlineDelete: boolean;
        onDelete: ((id: string) => void) | undefined;
        onDeleteHandle: ((row: GridRenderCellParams['row']) => void) | undefined;
        handleDeleteClick?: (row: GridRenderCellParams['row']) => void;
    };
    copyHandlers: {
        onCopy: ((row: GridRenderCellParams['row']) => void) | undefined;
        copyTooltipTitle: string;
    };
    customActions: Array<(params: GridRenderCellParams) => ReactElement<GridActionsCellItemProps>>;
    customRowActions?: DataGridCustomRowAction[];
    isMobile: boolean;
    actionLabels: {
        saveLabel: string;
        cancelLabel: string;
        editLabel: string;
        shareLabel: string;
        copyLabel: string;
        deleteLabel: string;
    };
};

export type DataGridActionsContextType = {
    apiRef: RefObject<GridApiPremium>;
    entityName?: EntityName;
    feature: string;
    activeRow: GridRowModel | undefined;
    isRowValid: ((id: string) => boolean) | undefined;
    onEdit: ((id: string, row: GridRowModel) => void) | undefined;
    onShare: ((row: GridRowModel) => void) | undefined;
    enableInlineEditing: boolean;
    enableInlineDelete: boolean;
    onDelete: ((id: string) => void) | undefined;
    onDeleteHandle: ((row: GridRowModel) => void) | undefined;
    copyTooltipTitle: string;
    onCopy: ((row: GridRowModel) => void) | undefined;
    onActivateOrInactivateHandle:
        | ((row: GridRowModel, canInactivate: boolean, canActivate: boolean) => void)
        | undefined;
    customActions: Array<(params: GridRenderCellParams) => ReactElement<GridActionsCellItemProps>>;
    customRowActions: DataGridCustomRowAction[];
    isMobile: boolean;
    actionLabels: {
        saveLabel: string;
        cancelLabel: string;
        editLabel: string;
        shareLabel: string;
        copyLabel: string;
        deleteLabel: string;
    };
    handleEditClick?: (id: GridRowId) => void;
    handleEditCommit?: (id: GridRowId) => void;
    handleEditCancel?: (id: GridRowId) => void;
    handleDeleteClick?: (row: GridRowModel) => void;
hideEditActionWhenInlineEditing?: boolean;
};
export enum RowAction {
    Add,
    Edit,
    Delete
}

export type Toolbar = {
    export?: boolean;
    columns?: boolean;
    add?: boolean;
    custom?: any[];
    presets?: {
        visible: boolean;
        labelToken?: string;
    };
};
export type ToolbarOptions = {
    printOptions?: GridPrintExportOptions;
    csvOptions?: GridCsvExportOptions;
};

export type DataGridProps = {
    id: string;
    feature: string;
    /**
     * Optional only to support callers that still rely on the legacy permission fallback.
     * Make required once those callers forward it and the missing-entity fallback is removed.
     */
    entityName?: EntityName;
    rows: GridRowModel[]; // GridRowsProp<R>
    columns: GridColDef[];
    rowCount?: number;
    /** scrollEndThreshold prop to control when the infinite loading or lazy loading callback is triggered. */
    scrollEndThreshold?: number;
    /** onScrollEnd prop (mapping to MUI DataGridPremium's onRowsScrollEnd) to detect when the user scrolls to the bottom of the grid's vertical scroll area, commonly used for infinite loading. */ onScrollEnd?: () => void;
    setRows?: (rows: GridRowModel[]) => void;
    includeActions?: boolean;
    addTitle?: string | ReactElement;
    onAdd?: () => void;
    onEdit?: (id: number | string, row: GridRowModel) => void;
    onShare?: (row: GridRowModel) => void;
    onCopy?: (row: GridRowModel) => void;
    onDelete?: (id: number | string) => void;
    copyTooltipTitle?: string;
    onDeleteHandle?: (row: GridRowModel) => void;
    onDetails?: (id: number | string, row?: GridRowModel) => void;
    sortingMode?: GridFeatureMode;
    serverPagination?: boolean;
    toolbar?: Toolbar;
    toolbarOptions?: ToolbarOptions;
    toolbarSx?: SxProps;
    onColumnOrderChange?: (allColumns: GridColDef[], event: MuiEvent<{}>) => void;
    disablePagination?: boolean;
    hideFooterRowCount?: boolean;
    disableColumnResize?: boolean;
    disableColumnSorting?: boolean;
    enableInlineEditing?: boolean;
    hideEditActionWhenInlineEditing?: boolean;
    errors?: any;
    asIsColumns?: string[];
    page?: number;
    sortModel?: GridSortModel;
    filterModel?: GridFilterModel;
    onSortModelChange?: (model: GridSortModel) => void;
    onPageChange?: (page: number) => void;
    pageSize?: number;
    onPageSizeChange?: (page: number) => void;
    clickableColumn?: string;
    loading?: boolean;
    getRowClassName?: (params: GridRowClassNameParams) => string;
    defaultColumnVisibility?: GridColumnVisibilityModel;
    columnVisibilityPresets?: ColumnVisibilityPreset[];
    onActivateOrInactivateHandle?: (row, isInactivate, isActivate) => void;
    /**
     * @deprecated Use `customRowActions` for RBAC-gated row actions.
     * Legacy pattern: receives grid columns on params; relies on old permission model.
     * Will be removed after all call sites migrate to `customRowActions`.
     */
    customActions?: Array<(params: GridRowParams) => ReactElement<GridActionsCellItemProps>>;
    /**
     * RBAC-gated row actions with built-in security evaluation.
     * Each action specifies a `securityAction` that integrates with centralized RBAC.
     * When `entityName` is provided, permissions are evaluated via the centralized security framework.
     * When `entityName` is omitted, action visibility falls back to the legacy feature + row permissions model.
     * Preferred pattern for new features and migrations.
     *
     * When using the centralized security framework a per-action `featureName` can also be passed.
     * If passed, the action will be evaluated against the specified feature in addition to the entity's permissions.
     * In not passed, the security evaluation will use the feature name from the ambient feature security context,
     * i.e. the nearest wrapping `FeatureSecurityProvider`.
     */
    customRowActions?: DataGridCustomRowAction[];
    containerSx?: SxProps;
    onCreateNewRow?: (row: GridRowModel) => void;
    isRowValid?: (row) => boolean;
    append?: boolean;
    rowReordering?: boolean;
    onRowOrderChange?: (params: GridRowOrderChangeParams) => void;
    hideFooterSelectedRowCount?: boolean;
    hideHeader?: boolean;
    hideFooter?: boolean;
    autoHeight?: boolean;
    disableVirtualization?: boolean;
    slots?: Partial<GridSlotsComponent>;
    slotProps?: any;
    getDetailPanelHeight?: (params: GridRowParams<any>) => number | 'auto';
    getDetailPanelContent?: (params: GridRowParams<unknown>) => React.ReactNode;
    disableColumnMenu?: boolean;
    disableColumnFilter?: boolean;
    density?: GridDensity;
    disableRowSelectionOnClick?: boolean;
    checkboxSelection?: boolean;
    onRowSelectionModelChange?: (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any>) => void;
    isRowSelectable?: (params: GridRowParams<GridValidRowModel>) => boolean;
    newRowOnTop?: boolean;
    rowSelectionModel?: GridRowSelectionModel;
    forceDefaultColumnVisibility?: boolean;
    inActivateOnBlur?: boolean;
    commitRowOnBlur?: boolean;
    /** When true, row focus-out commits only if editable field values changed. Requires `commitRowOnBlur`. */
    commitRowOnBlurOnlyWhenDirty?: boolean;
    /** Returns true while the row is already being committed, preventing duplicate save attempts. */
    getIsRowCommitInProgress?: (id: GridRowId) => boolean;
    /** Returns true to keep the row in edit mode and skip blur commit for external editor workflows. */
    getShouldSuppressRowCommitOnBlur?: (params: GridRowEditStopParams, event: MuiEvent<SyntheticEvent>) => boolean;
    /** When true, exits row edit mode before async persistence resolves so the row can show a separate saving state. */
    exitEditModeBeforeProcessRowUpdate?: boolean;
    disableAdd?: boolean;
    triggerEditMode?: boolean;
    onDynamicRowAdd?: (apiRef: React.MutableRefObject<GridApiPremium>) => void;
    detailPanelExpandedRowIds?: GridRowId[];
    handleDetailPanelExpandedRowIdsChange?: (ids) => void;
    aggregationModel?: GridAggregationModel;
    paginationModel?: GridPaginationModel;
    processRowUpdate?: (update: GridRowModel, original: GridRowModel) => GridRowModel | Promise<GridRowModel>;
    rowGroupingModel?: GridRowGroupingModel;
    onRowGroupingModelChange?: (newModel: GridRowGroupingModel) => void;
    defaultGroupingExpansionDepth?: number;
    isGroupedColumnsHidden?: boolean;
    defaultSelectedPreset?: number;
    isCellClickable?: (params: GridRowParams & { field: string }) => boolean | undefined;
    /** Determines if a cell is editable based on row data and field. Return true to allow editing, false to disable. */
    isCellEditable?: (params: GridCellParams) => boolean;
    enableInlineDelete?: boolean;
    enableRichTextEditing?: boolean;
    /** When true, the grid will automatically scroll to the rightmost position. */
    shouldScrollRight?: boolean;
    /** @deprecated please use setRows */
    handleRowAction?: (action: RowAction, update: GridRowModel, original: GridRowModel) => void; // DO NOT USE THIS!;
    enabledFavouritePreset?: boolean;
    groupForPreset?: Array<{ label: string; value: Array<string> }>;
    defaultSelectedPresetLabel?: string;
    hideMyColumnsPreset?: boolean;
    hideAllColumnsPreset?: boolean;
    hideDefaultColumnsPreset?: boolean;
    customAggregationFunctions?: Record<string, GridAggregationFunction>;
    /**
     * When true (default), truncated cell values show a hover tooltip.
     * When false, cell tooltips always show on hover (legacy data-import behavior).
     */
    noWrapColValue?: boolean;
    externalColumnVisibilityModel?: GridColumnVisibilityModel; // controlled
    externalOnColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
    onRowEditStart?: () => void;
    onRowEditStop?: () => void;
    /**
     * Optional configuration for rendering a compact "list view" instead of the full data grid.
     * - `enableListView`: when true the grid can render rows using the provided `listViewColDef`.
     * - `listViewColDef`: a `GridListViewColDef` describing the single column/renderCell used for list-style rendering.
     */
    listViewConfiguration?: {
        enableListView: boolean;
        listViewColDef: GridListViewColDef;
        rowBufferPx?: number;
    };
    /**
     * When this value changes (e.g. increment), invokes the same handler as the toolbar add button (createNewRow):
     * inline add when enableInlineEditing and no row is active, otherwise onAdd() if provided.
     * Does not run when disableAdd is true.
     */
    externalAddTriggerValue?: number;
    groupingColDef?: GridGroupingColDefOverride<GridValidRowModel>;
    /**
     * Opt in to prefilling new inline rows from the active view filters (create only).
     * A column participates when its field matches a facet identifier; single-select prefills
     * only for exactly one active value, multi-select prefills all of them.
     */
    createDefaultsFromViewContext?: CreateDefaultsFromViewContextConfig;
    /**
     * When true, autosize columns once rows are ready and apply the pending/filler styles so
     * default-width columns are not shown during the first measure pass. Column `flex` /
     * `minWidth` are kept as empty-column fallbacks: omitted (with a console warning) only
     * for columns that have at least one non-empty cell. Empty columns keep `minWidth` so
     * headers stay readable; a fully empty grid also keeps `flex` so columns still fill the
     * width. Default false.
     */
    autoSizeColumns?: boolean;
    /**
     * When true, persist autosize vs manual column sizing and include an Autosize switch in the
     * grid settings modal. Also enables `preserveAutosize` and `preserveColumnWidths`.
     * Default false.
     */
    preserveConfiguration?: boolean;
    /** When true, the Autosize choice is saved to the personal column-layout preference. */
    preserveAutosize?: boolean;
    /** When true, a manual column resize is saved to the personal column-layout preference. */
    preserveColumnWidths?: boolean;
    /**
     * When true, hiding or showing columns can be saved as a named view.
     * Saved views are scoped by application and shared across grids with the same `id`.
     * Default false.
     */
    enableSaveColumnView?: boolean;
    /**
     * When true, column order is stored on the saved view and reordering marks that view dirty.
     * Independent of sort and group. Default false.
     */
    enableColumnOrder?: boolean;
    /**
     * When true, the grid settings modal includes sort and group.
     * The settings gear is also shown when `preserveConfiguration` is on. Default false.
     */
    enableGridSettings?: boolean;
    /** Per-grid sort/group options for the settings modal. Custom appears only if included. */
    gridSettings?: GridSettingsConfig;
    /**
     * Display name for the settings modal title (`"{name} Settings"`).
     * Example: "Line Item" → "Line Item Settings".
     */
    settingsEntityName?: string;
};

export type CreateDefaultsFromViewContextConfig = {
    /** Facets of the current view; limits participation to user-facing filters. */
    facets?: FacetForCreateDefaults[];
    /** Cardinality / value-shape metadata for custom attribute fields. */
    fieldMetadata?: CreateDefaultsFieldMetadata[];
};
